import React, { useState } from "react";
import cx from "classnames";
import { Trans, t, select } from "@lingui/macro";
import Tooltip from "../Tooltip/Tooltip";
import PositionSeller from "./PositionSeller";
import PositionEditor from "./PositionEditor";
import OrdersToa from "./OrdersToa";

import { ImSpinner2 } from "react-icons/im";

import {
  getLiquidationPrice,
  getLeverage,
  getOrderError,
  USD_DECIMALS,
  FUNDING_RATE_PRECISION,
  SWAP,
  LONG,
  SHORT,
  INCREASE,
  DECREASE,
} from "lib/legacy";
import PositionShare from "./PositionShare";
import PositionDropdown from "./PositionDropdown";
import StatsTooltipRow from "../StatsTooltip/StatsTooltipRow";
import { helperToast } from "lib/helperToast";
import { getUsd } from "domain/tokens/utils";
import { bigNumberify, formatAmount } from "lib/numbers";
import { AiOutlineEdit } from "react-icons/ai";
import useAccountType, { AccountType } from "lib/wallets/useAccountType";

const getOrdersForPosition = (account, position, orders, nativeTokenAddress) => {
  if (!orders || orders.length === 0) {
    return [];
  }
  /* eslint-disable array-callback-return */
  return orders
    .filter((order) => {
      if (order.type === SWAP) {
        return false;
      }
      const hasMatchingIndexToken =
        order.indexToken === nativeTokenAddress
          ? position.indexToken.isNative
          : order.indexToken === position.indexToken.address;
      const hasMatchingCollateralToken =
        order.collateralToken === nativeTokenAddress
          ? position.collateralToken.isNative
          : order.collateralToken === position.collateralToken.address;
      if (order.isLong === position.isLong && hasMatchingIndexToken && hasMatchingCollateralToken) {
        return true;
      }
    })
    .map((order) => {
      order.error = getOrderError(account, order, undefined, position);
      if (order.type === DECREASE && order.sizeDelta.gt(position.size)) {
        order.error = `Order size is bigger than position, will only be executable if position increases`;
      }
      return order;
    });
};

export default function PositionsList(props) {
  const {
    pendingPositions,
    setPendingPositions,
    positions,
    positionsDataIsLoading,
    positionsMap,
    infoTokens,
    active,
    account,
    library,
    pendingTxns,
    setPendingTxns,
    setListSection,
    flagOrdersEnabled,
    savedIsPnlInLeverage,
    chainId,
    nativeTokenAddress,
    orders,
    setIsWaitingForPluginApproval,
    approveOrderBook,
    isPluginApproving,
    isWaitingForPluginApproval,
    orderBookApproved,
    positionRouterApproved,
    isWaitingForPositionRouterApproval,
    isPositionRouterApproving,
    approvePositionRouter,
    showPnlAfterFees,
    setMarket,
    minExecutionFee,
    minExecutionFeeUSD,
    minExecutionFeeErrorMessage,
    usdgSupply,
    totalTokenWeights,
  } = props;

  const [positionToEditKey, setPositionToEditKey] = useState(undefined);
  const [positionToSellKey, setPositionToSellKey] = useState(undefined);
  const [positionToShare, setPositionToShare] = useState(null);
  const [isPositionEditorVisible, setIsPositionEditorVisible] = useState(undefined);
  const [isPositionSellerVisible, setIsPositionSellerVisible] = useState(undefined);
  const [collateralTokenAddress, setCollateralTokenAddress] = useState(undefined);
  const [isPositionShareModalOpen, setIsPositionShareModalOpen] = useState(false);
  const [ordersToaOpen, setOrdersToaOpen] = useState(false);
  const [isHigherSlippageAllowed, setIsHigherSlippageAllowed] = useState(false);
  const accountType = useAccountType();
  const isContractAccount = accountType === AccountType.CONTRACT;

  const editPosition = (position) => {
    setCollateralTokenAddress(position.collateralToken.address);
    setPositionToEditKey(position.key);
    setIsPositionEditorVisible(true);
  };

  const sellPosition = (position) => {
    setPositionToSellKey(position.key);
    setIsPositionSellerVisible(true);
    setIsHigherSlippageAllowed(false);
  };

  const onPositionClick = (position) => {
    helperToast.success(
      <span>
        {position.isLong ? "Long" : "Short"} {position.indexToken.symbol} market selected
      </span>
    );
    setMarket(position.isLong ? LONG : SHORT, position.indexToken.address);
  };

  return (
    <div className="PositionsList">
      <PositionEditor
        pendingPositions={pendingPositions}
        setPendingPositions={setPendingPositions}
        positionsMap={positionsMap}
        positionKey={positionToEditKey}
        isVisible={isPositionEditorVisible}
        setIsVisible={setIsPositionEditorVisible}
        infoTokens={infoTokens}
        active={active}
        account={account}
        library={library}
        collateralTokenAddress={collateralTokenAddress}
        pendingTxns={pendingTxns}
        setPendingTxns={setPendingTxns}
        getUsd={getUsd}
        getLeverage={getLeverage}
        savedIsPnlInLeverage={savedIsPnlInLeverage}
        positionRouterApproved={positionRouterApproved}
        isPositionRouterApproving={isPositionRouterApproving}
        isWaitingForPositionRouterApproval={isWaitingForPositionRouterApproval}
        approvePositionRouter={approvePositionRouter}
        chainId={chainId}
        minExecutionFee={minExecutionFee}
        minExecutionFeeUSD={minExecutionFeeUSD}
        minExecutionFeeErrorMessage={minExecutionFeeErrorMessage}
        isContractAccount={isContractAccount}
      />
      {ordersToaOpen && (
        <OrdersToa
          setIsVisible={setOrdersToaOpen}
          approveOrderBook={approveOrderBook}
          isPluginApproving={isPluginApproving}
        />
      )}
      {/* {" "} */}
      {isPositionShareModalOpen && (
        <PositionShare
          setIsPositionShareModalOpen={setIsPositionShareModalOpen}
          isPositionShareModalOpen={isPositionShareModalOpen}
          positionToShare={positionToShare}
          chainId={chainId}
          account={account}
        />
      )}
      {ordersToaOpen && (
        <OrdersToa
          setIsVisible={setOrdersToaOpen}
          approveOrderBook={approveOrderBook}
          isPluginApproving={isPluginApproving}
        />
      )}
      {isPositionSellerVisible && (
        <PositionSeller
          pendingPositions={pendingPositions}
          setPendingPositions={setPendingPositions}
          setIsWaitingForPluginApproval={setIsWaitingForPluginApproval}
          approveOrderBook={approveOrderBook}
          isPluginApproving={isPluginApproving}
          isWaitingForPluginApproval={isWaitingForPluginApproval}
          orderBookApproved={orderBookApproved}
          positionsMap={positionsMap}
          positionKey={positionToSellKey}
          isVisible={isPositionSellerVisible}
          setIsVisible={setIsPositionSellerVisible}
          infoTokens={infoTokens}
          active={active}
          account={account}
          orders={orders}
          library={library}
          pendingTxns={pendingTxns}
          setPendingTxns={setPendingTxns}
          flagOrdersEnabled={flagOrdersEnabled}
          savedIsPnlInLeverage={savedIsPnlInLeverage}
          chainId={chainId}
          nativeTokenAddress={nativeTokenAddress}
          setOrdersToaOpen={setOrdersToaOpen}
          positionRouterApproved={positionRouterApproved}
          isPositionRouterApproving={isPositionRouterApproving}
          isWaitingForPositionRouterApproval={isWaitingForPositionRouterApproval}
          approvePositionRouter={approvePositionRouter}
          isHigherSlippageAllowed={isHigherSlippageAllowed}
          setIsHigherSlippageAllowed={setIsHigherSlippageAllowed}
          minExecutionFee={minExecutionFee}
          minExecutionFeeUSD={minExecutionFeeUSD}
          minExecutionFeeErrorMessage={minExecutionFeeErrorMessage}
          usdgSupply={usdgSupply}
          totalTokenWeights={totalTokenWeights}
          isContractAccount={isContractAccount}
        />
      )}
      {positions && !positionsDataIsLoading && (
        <div className="Exchange-list small">
          <div>
            {positions.length === 0 && positionsDataIsLoading && (
              <div className="Exchange-empty-positions-list-note App-card">
                <div className="skeleton-box" style={{ height: "25px" }} />
              </div>
            )}
            {positions.length === 0 && !positionsDataIsLoading && (
              <div className="Exchange-empty-positions-list-note App-card no-data-card">
                <span>No open positions</span>
              </div>
            )}
            {positions.map((position) => {
              const positionOrders = getOrdersForPosition(account, position, orders, nativeTokenAddress);
              const liquidationPrice = getLiquidationPrice(position);
              const hasPositionProfit = position[showPnlAfterFees ? "hasProfitAfterFees" : "hasProfit"];
              const positionDelta =
                position[showPnlAfterFees ? "pendingDeltaAfterFees" : "pendingDelta"] || bigNumberify(0);
              let borrowFeeUSD;
              if (position.collateralToken && position.collateralToken.fundingRate) {
                const borrowFeeRate = position.collateralToken.fundingRate
                  .mul(position.size)
                  .mul(24)
                  .div(FUNDING_RATE_PRECISION);
                borrowFeeUSD = formatAmount(borrowFeeRate, USD_DECIMALS, 2);
              }

              return (
                <div key={position.key} className="App-card">
                  <div className="App-card-title">
                    <span className="Exchange-list-title">{position.indexToken.symbol}</span>
                  </div>
                  <div className="App-card-divider" />
                  <div className="App-card-content app-card-position">
                    {/* <div className="App-card-row">
                      <div className="label">
                        <span>Leverage</span>
                      </div>
                      <div>
                        {formatAmount(position.leverage, 4, 2, true)}x&nbsp;
                        <span
                          className={cx("Exchange-list-side", {
                            positive: position.isLong,
                            negative: !position.isLong,
                          })}
                        >
                          {position.isLong ? `Long` : `Short`}
                        </span>
                      </div>
                    </div> */}
                    <div className="leverage-pad">
                      <div
                        className={cx("Exchange-list-side", {
                          positive: position.isLong,
                          negative: !position.isLong,
                        })}
                      >
                        {position.isLong ? "LONG" : SHORT}
                      </div>
                      <div className="tab-leveage">{formatAmount(position.leverage, 4, 2, true)}X</div>
                    </div>
                    <div className="App-card-row">
                      <div className="label">
                        <span>Size</span>
                      </div>
                      <div>${formatAmount(position.size, USD_DECIMALS, 2, true)}</div>
                    </div>
                    <div className="App-card-row">
                      <div className="label">
                        <span>Collateral</span>
                      </div>
                      <div className="position-list-collateral">
                        <Tooltip
                          handle={`$${formatAmount(position.collateralAfterFee, USD_DECIMALS, 2, true)}`}
                          position="right-bottom"
                          handleClassName={cx("plain", { negative: position.hasLowCollateral })}
                          renderContent={() => {
                            return (
                              <>
                                {position.hasLowCollateral && (
                                  <div>
                                    <span>
                                      WARNING: This position has a low amount of collateral after deducting borrowing
                                      fees, deposit more collateral to reduce the position's liquidation risk.
                                    </span>
                                    <br />
                                    <br />
                                  </div>
                                )}
                                <StatsTooltipRow
                                  label={`Initial Collateral`}
                                  value={formatAmount(position.collateral, USD_DECIMALS, 2, true)}
                                />
                                <StatsTooltipRow
                                  label={`Borrow Fee`}
                                  value={formatAmount(position.fundingFee, USD_DECIMALS, 2, true)}
                                />
                                <StatsTooltipRow label={`Borrow Fee / Day`} value={borrowFeeUSD} />
                                <span>
                                  <span>Use the Edit Collateral icon to deposit or withdraw collateral.</span>
                                </span>
                              </>
                            );
                          }}
                        />
                        <span className="edit-icon" onClick={() => editPosition(position)}>
                          <AiOutlineEdit fontSize={16} />
                        </span>
                      </div>
                    </div>
                    <div className="App-card-row">
                      <div className="label">
                        <span>PnL</span>
                      </div>
                      <div>
                        <span
                          className={cx("Exchange-list-info-label", {
                            positive: hasPositionProfit && positionDelta.gt(0),
                            negative: !hasPositionProfit && positionDelta.gt(0),
                            muted: positionDelta.eq(0),
                          })}
                        >
                          {position.deltaStr} ({position.deltaPercentageStr})
                        </span>
                      </div>
                    </div>
                    <div className="App-card-row">
                      <div className="label">
                        <span>Net Value</span>
                      </div>
                      <div>
                        <Tooltip
                          handle={`$${formatAmount(position.netValue, USD_DECIMALS, 2, true)}`}
                          position="right-bottom"
                          handleClassName="plain"
                          renderContent={() => {
                            return (
                              <>
                                {showPnlAfterFees
                                  ? `Net Value: Initial Collateral - Fees + PnL`
                                  : `Net Value: Initial Collateral - Borrow Fee + PnL`}
                                <br />
                                <br />
                                <StatsTooltipRow
                                  label={`Initial Collateral`}
                                  value={formatAmount(position.collateral, USD_DECIMALS, 2, true)}
                                />
                                <StatsTooltipRow label={`PnL`} value={position.deltaBeforeFeesStr} showDollar={false} />
                                <StatsTooltipRow
                                  label={`Borrow Fee`}
                                  value={formatAmount(position.fundingFee, USD_DECIMALS, 2, true)}
                                />
                                <StatsTooltipRow
                                  label={`Open + Close fee`}
                                  value={formatAmount(position.positionFee, USD_DECIMALS, 2, true)}
                                />
                                <StatsTooltipRow
                                  label={`PnL After Fees`}
                                  value={`${position.deltaAfterFeesStr} (${position.deltaAfterFeesPercentageStr})`}
                                  showDollar={false}
                                />
                              </>
                            );
                          }}
                        />
                      </div>
                    </div>
                    <div className="App-card-row">
                      <div className="label">
                        <span>Orders</span>
                      </div>
                      <div>
                        {positionOrders.length === 0 && "None"}
                        {positionOrders.map((order) => {
                          const orderText = () => (
                            <>
                              {order.triggerAboveThreshold ? ">" : "<"}{" "}
                              {formatAmount(order.triggerPrice, 30, position?.indexToken?.displayDecimals || 2, true)}:
                              {order.type === INCREASE ? " +" : " -"}${formatAmount(order.sizeDelta, 30, 2, true)}
                            </>
                          );
                          if (order.error) {
                            return (
                              <div key={`${order.isLong}-${order.type}-${order.index}`} className="Position-list-order">
                                <Tooltip
                                  className="order-error"
                                  handle={orderText()}
                                  position="right-bottom"
                                  handleClassName="plain"
                                  renderContent={() => <span className="negative">{order.error}</span>}
                                />
                              </div>
                            );
                          } else {
                            return (
                              <div key={`${order.isLong}-${order.type}-${order.index}`} className="Position-list-order">
                                {orderText()}
                              </div>
                            );
                          }
                        })}
                      </div>
                    </div>
                  </div>
                  {/* <div className="App-card-divider" /> */}
                  <div style={{ marginTop: "0.8rem" }} />
                  <div className="App-card-content">
                    <div className="App-card-row">
                      <div className="label">
                        <span>Mark Price</span>
                      </div>
                      <div>
                        $
                        {formatAmount(
                          position.markPrice,
                          USD_DECIMALS,
                          position?.indexToken?.displayDecimals || 2,
                          true
                        )}
                      </div>
                    </div>
                    <div className="App-card-row">
                      <div className="label">
                        <span>Entry Price</span>
                      </div>
                      <div>
                        $
                        {formatAmount(
                          position.averagePrice,
                          USD_DECIMALS,
                          position?.indexToken?.displayDecimals || 2,
                          true
                        )}
                      </div>
                    </div>
                    <div className="App-card-row">
                      <div className="label">
                        <span>Liq. Price</span>
                      </div>
                      <div>
                        $
                        {formatAmount(liquidationPrice, USD_DECIMALS, position?.indexToken?.displayDecimals || 2, true)}
                      </div>
                    </div>
                  </div>
                  {/* <div className="App-card-divider"></div> */}
                  <div style={{ marginTop: "1.3rem" }} />
                  <div className="App-card-options">
                    <button
                      className="App-button-option App-card-option"
                      disabled={position.size.eq(0)}
                      onClick={() => sellPosition(position)}
                    >
                      <span>Close</span>
                    </button>
                    <button
                      className="App-button-option App-card-option border-btn"
                      disabled={position.size.eq(0)}
                      onClick={() => editPosition(position)}
                    >
                      <span>Edit Collateral</span>
                    </button>
                    <button
                      className="Exchange-list-action App-button-option App-card-option border-btn"
                      onClick={() => {
                        setPositionToShare(position);
                        setIsPositionShareModalOpen(true);
                      }}
                      disabled={position.size.eq(0)}
                    >
                      <span>Share</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {positionsDataIsLoading && <div className="skeleton-box" style={{ height: "25px" }} />}
      {!positionsDataIsLoading && (
        <table className={`Exchange-list large App-box ${positions.length === 0 ? "no-array-length" : ""}`}>
          <tbody>
            {positions.length > 0 && (
              <tr className="Exchange-list-header">
                <th>
                  <span>Position</span>
                </th>
                <th>
                  <span>Net Value</span>
                </th>
                <th>
                  <span>Size</span>
                </th>
                <th>
                  <span>Collateral</span>
                </th>
                <th>
                  <span>Mark Price</span>
                </th>
                <th>
                  <span>Entry Price</span>
                </th>
                <th>
                  <span>Liq. Price</span>
                </th>
                <th></th>
              </tr>
            )}
            {positions.length === 0 && !positionsDataIsLoading && (
              <tr className="no-position">
                <td colSpan="15">
                  <div className="Exchange-empty-positions-list-note App-card no-position-card no-data-card">
                    <span>No open positions</span>
                  </div>
                </td>
              </tr>
            )}
            {positions &&
              positions.map((position) => {
                // console.log('####position',position);
                const liquidationPrice = getLiquidationPrice(position) || bigNumberify(0);
                const positionOrders = getOrdersForPosition(account, position, orders, nativeTokenAddress);
                const hasOrderError = !!positionOrders.find((order) => order.error);
                const hasPositionProfit = position[showPnlAfterFees ? "hasProfitAfterFees" : "hasProfit"];
                const positionDelta =
                  position[showPnlAfterFees ? "pendingDeltaAfterFees" : "pendingDelta"] || bigNumberify(0);
                let borrowFeeUSD;
                if (position.collateralToken && position.collateralToken.fundingRate) {
                  const borrowFeeRate = position.collateralToken.fundingRate
                    .mul(position.size)
                    .mul(24)
                    .div(FUNDING_RATE_PRECISION);
                  borrowFeeUSD = formatAmount(borrowFeeRate, USD_DECIMALS, 2);
                }

                return (
                  <tr key={position.key} className="table-content">
                    <td className="clickable" onClick={() => onPositionClick(position)}>
                      <div className="Exchange-list-title">
                        {position.indexToken.symbol}
                        {position.hasPendingChanges && <ImSpinner2 className="spin position-loading-icon" />}
                      </div>
                      <div className="Exchange-list-info-label">
                        <span
                          className={cx({ positive: position.isLong, negative: !position.isLong })}
                          style={{ color: position.isLong ? "#03F5AE" : "#F6475D" }}
                        >
                          {position.isLong ? `LONG` : `SHORT`}
                        </span>
                        {position.leverage && (
                          <span
                            className="muted"
                            style={{
                              color: "#FFC700",
                              backgroundColor: "#FFC70033",
                              opacity: 1,
                              borderRadius: "4px",
                              padding: "0 4px",
                              fontWeight: 600,
                              marginLeft: "4px",
                            }}
                          >
                            {formatAmount(position.leverage, 4, 2)}X
                          </span>
                        )}
                      </div>
                      <div className="flag" style={{ background: position.isLong ? "#03F5AE" : "#F6475D" }}></div>
                    </td>
                    <td>
                      <div>
                        {!position.netValue && `Opening...`}
                        {position.netValue && (
                          <Tooltip
                            handle={`$${formatAmount(position.netValue, USD_DECIMALS, 2, true)}`}
                            position="left-bottom"
                            handleClassName="plain"
                            renderContent={() => {
                              return (
                                <>
                                  {showPnlAfterFees
                                    ? `Net Value: Initial Collateral - Fees + PnL`
                                    : `Net Value: Initial Collateral - Borrow Fee + PnL`}
                                  <br />
                                  <br />
                                  <StatsTooltipRow
                                    label={`Initial Collateral`}
                                    value={formatAmount(position.collateral, USD_DECIMALS, 2, true)}
                                  />
                                  <StatsTooltipRow
                                    label={`PnL`}
                                    value={position.deltaBeforeFeesStr}
                                    showDollar={false}
                                  />
                                  <StatsTooltipRow
                                    label={`Borrow Fee`}
                                    showDollar={false}
                                    value={`-$${formatAmount(position.fundingFee, USD_DECIMALS, 2, true)}`}
                                  />
                                  <StatsTooltipRow
                                    label={`Open + Close fee`}
                                    showDollar={false}
                                    value={`-$${formatAmount(position.positionFee, USD_DECIMALS, 2, true)}`}
                                  />
                                  <StatsTooltipRow
                                    label={`PnL After Fees`}
                                    value={`${position.deltaAfterFeesStr} (${position.deltaAfterFeesPercentageStr})`}
                                    showDollar={false}
                                  />
                                </>
                              );
                            }}
                          />
                        )}
                      </div>
                      {position.deltaStr && (
                        <div
                          className={cx("Exchange-list-info-label", {
                            positive: hasPositionProfit && positionDelta.gt(0),
                            negative: !hasPositionProfit && positionDelta.gt(0),
                            muted: positionDelta.eq(0),
                          })}
                        >
                          {position.deltaStr} ({position.deltaPercentageStr})
                        </div>
                      )}
                    </td>
                    <td>
                      <div>${formatAmount(position.size, USD_DECIMALS, 2, true)}</div>
                      {positionOrders.length > 0 && (
                        <div onClick={() => setListSection && setListSection("Orders")}>
                          <Tooltip
                            handle={`Orders (${positionOrders.length})`}
                            position="left-bottom"
                            handleClassName={cx(
                              ["Exchange-list-info-label", "Exchange-position-list-orders", "plain", "clickable"],
                              { muted: !hasOrderError, negative: hasOrderError }
                            )}
                            renderContent={() => {
                              return (
                                <>
                                  <strong>
                                    <span>Active Orders</span>
                                  </strong>
                                  {positionOrders.map((order) => {
                                    return (
                                      <div
                                        key={`${order.isLong}-${order.type}-${order.index}`}
                                        className="Position-list-order"
                                      >
                                        {order.triggerAboveThreshold ? "≥" : "≤"}{" "}
                                        {formatAmount(
                                          order.triggerPrice,
                                          30,
                                          position?.indexToken?.displayDecimals || 2,
                                          true
                                        )}
                                        :{order.type === INCREASE ? " +" : " -"}$
                                        {formatAmount(order.sizeDelta, 30, 2, true)}
                                        {order.error && (
                                          <>
                                            , <span className="negative">{order.error}</span>
                                          </>
                                        )}
                                      </div>
                                    );
                                  })}
                                </>
                              );
                            }}
                          />
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="position-list-collateral">
                        <Tooltip
                          handle={`$${formatAmount(position.collateralAfterFee, USD_DECIMALS, 2, true)}`}
                          position="left-bottom"
                          handleClassName={cx("plain", { negative: position.hasLowCollateral })}
                          renderContent={() => {
                            return (
                              <>
                                {position.hasLowCollateral && (
                                  <div>
                                    <span>
                                      WARNING: This position has a low amount of collateral after deducting borrowing
                                      fees, deposit more collateral to reduce the position's liquidation risk.
                                    </span>
                                    <br />
                                    <br />
                                  </div>
                                )}

                                <StatsTooltipRow
                                  label={`Initial Collateral`}
                                  value={formatAmount(position.collateral, USD_DECIMALS, 2, true)}
                                />
                                <StatsTooltipRow
                                  label={`Borrow Fee`}
                                  showDollar={false}
                                  value={`-$${formatAmount(position.fundingFee, USD_DECIMALS, 2, true)}`}
                                />
                                <StatsTooltipRow
                                  showDollar={false}
                                  label={`Borrow Fee / Day`}
                                  value={`-$${borrowFeeUSD}`}
                                />
                                <br />
                                <span>Use the Edit Collateral icon to deposit or withdraw collateral.</span>
                              </>
                            );
                          }}
                        />
                        <span className="edit-icon" onClick={() => editPosition(position)}>
                          <AiOutlineEdit fontSize={16} />
                        </span>
                      </div>
                    </td>
                    <td className="clickable" onClick={() => onPositionClick(position)}>
                      <Tooltip
                        handle={`$${formatAmount(
                          position.markPrice,
                          USD_DECIMALS,
                          position?.indexToken?.displayDecimals || 2,
                          true
                        )}`}
                        position="left-bottom"
                        handleClassName="plain clickable"
                        renderContent={() => {
                          return (
                            <div>
                              <span>
                                Click on a row to select the position's market, then use the swap box to increase your
                                position size if needed.
                              </span>
                              <br />
                              <br />
                              <span>
                                Use the "Close" button to reduce your position size, or to set stop-loss / take-profit
                                orders.
                              </span>
                            </div>
                          );
                        }}
                      />
                    </td>
                    <td className="clickable" onClick={() => onPositionClick(position)}>
                      $
                      {formatAmount(
                        position.averagePrice,
                        USD_DECIMALS,
                        position?.indexToken?.displayDecimals || 2,
                        true
                      )}
                    </td>
                    <td className="clickable" onClick={() => onPositionClick(position)}>
                      ${formatAmount(liquidationPrice, USD_DECIMALS, position?.indexToken?.displayDecimals || 2, true)}
                    </td>

                    <td style={{ width: "30px" }}>
                      <div className="close-container">
                        <button
                          className="Exchange-list-action"
                          onClick={() => sellPosition(position)}
                          disabled={position.size.eq(0)}
                        >
                          <span>Close</span>
                        </button>
                        <PositionDropdown
                          handleEditCollateral={() => {
                            editPosition(position);
                          }}
                          handleShare={() => {
                            setPositionToShare(position);
                            setIsPositionShareModalOpen(true);
                          }}
                          handleMarketSelect={() => {
                            onPositionClick(position);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      )}
    </div>
  );
}
